import { mockStudentStorage } from '../mockStudentStorage';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import { Avatar, Box, Button, ButtonBase, Stack, TextField, Typography } from '@mui/material';
import React from 'react';
import { useHistory } from 'react-router-dom';
import PhoneFrame from '../components/PhoneFrame';
import { completedLessonCount, mockCourses } from '../mockData';
import { normalizeStudentAvatarColor, studentAvatarColorNames, studentAvatarColors } from '../studentAvatarColors';
import { BLUE, blueButtonSx, cardSx, GREEN, INTER, NAVY, POPPINS, SLATE, SURFACE_SOFT } from '../studentStyles';

const overlineSx = { fontFamily: INTER, fontWeight: 600, fontSize: 11, letterSpacing: '0.08em', color: SLATE };
const fieldLabelSx = { fontFamily: INTER, fontWeight: 600, fontSize: 13, color: SLATE };


function StatTile({ icon, value, label }: { icon: React.ReactNode; value: string; label: string }) {
    return (
        <Box sx={{ flex: 1, p: 1.5, borderRadius: '14px', backgroundColor: SURFACE_SOFT, textAlign: 'center' }}>
            {icon}
            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 15, color: NAVY, mt: 0.25 }}>
                {value}
            </Typography>
            <Typography sx={{ fontFamily: INTER, fontSize: 11, color: SLATE }}>
                {label}
            </Typography>
        </Box>
    );
}

export default function StudentProfile() {
    const history = useHistory();

    const [name, setName] = React.useState(mockStudentStorage.getItem('studentName') || 'Maya Chen');
    const [avatarColor, setAvatarColor] = React.useState(() => normalizeStudentAvatarColor(mockStudentStorage.getItem('studentAvatarColor')));

    const lessonsDone = mockCourses.reduce((sum, course) => sum + completedLessonCount(course), 0);
    const exercisesDone = mockCourses.reduce(
        (sum, course) => sum + course.lessons.reduce((lessonSum, lesson) => lessonSum + lesson.completedPrompts, 0), 0);

    const handleSave = () => {
        // Prototype: persist to localStorage only — a real implementation would call UserApi.update()
        mockStudentStorage.setItem('studentName', name.trim());
        mockStudentStorage.setItem('studentAvatarColor', avatarColor);
        history.push('/mockup/v2/student', { notice: 'Profile saved.' });
    };

    const handleLogout = () => {
        history.push('/mockup/v2/student/login');
    };

    return (
        <PhoneFrame title="Profile" onBack={() => history.push('/mockup/v2/student')}>
            <Stack spacing={3}>

                {/* Profile header: who you are + progress at a glance */}
                <Box sx={{ ...cardSx, p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ width: 64, height: 64, bgcolor: avatarColor, fontFamily: POPPINS, fontWeight: 600, fontSize: 26 }}>
                            {(name || '?').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography noWrap sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 20, color: NAVY }}>
                                {name || 'New student'}
                            </Typography>
                            <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.25 }}>
                                Learning Chinese — keep it up!
                            </Typography>
                        </Box>
                    </Box>
                    <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
                        <StatTile
                            icon={<MenuBookRoundedIcon sx={{ color: BLUE, fontSize: 20 }} />}
                            value={`${lessonsDone}`}
                            label="lessons done"
                        />
                        <StatTile
                            icon={<CheckCircleRoundedIcon sx={{ color: GREEN, fontSize: 20 }} />}
                            value={`${exercisesDone}`}
                            label="exercises"
                        />
                    </Stack>
                </Box>

                {/* About you */}
                <Box sx={{ ...cardSx, p: 3 }}>
                    <Typography sx={overlineSx}>ABOUT YOU</Typography>
                    <TextField
                        fullWidth
                        label="Your name"
                        inputProps={{ maxLength: 40 }}
                        value={name}
                        onChange={event => setName(event.target.value)}
                        sx={{ mt: 2 }}
                    />
                    <Typography sx={{ ...fieldLabelSx, mt: 2.5, mb: 1.25 }}>
                        Avatar color
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        {studentAvatarColors.map(color => {
                            const selected = avatarColor === color;
                            return (
                                <ButtonBase
                                    key={color}
                                    aria-label={`${studentAvatarColorNames[color]} avatar color`}
                                    aria-pressed={selected}
                                    onClick={() => setAvatarColor(color)}
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: '50%',
                                        backgroundColor: color,
                                        border: '3px solid #fff',
                                        boxShadow: selected ? `0 0 0 2px ${NAVY}` : '0 2px 8px rgba(0,0,0,0.12)',
                                    }}
                                >
                                    {selected && <CheckRoundedIcon sx={{ color: '#fff', fontSize: 20 }} />}
                                </ButtonBase>
                            );
                        })}
                    </Box>
                </Box>

                {/* Lesson settings live on their own page, as on desktop. */}
                <Button
                    fullWidth
                    onClick={() => history.push('/mockup/v2/student/settings')}
                    startIcon={<TuneRoundedIcon />}
                    endIcon={<ChevronRightRoundedIcon />}
                    sx={{ ...cardSx, justifyContent: 'flex-start', p: 2.5, fontFamily: INTER, fontWeight: 700, fontSize: 14, color: NAVY, textTransform: 'none', '& .MuiButton-endIcon': { ml: 'auto' } }}
                >
                    Lesson settings
                </Button>

                <Button
                    fullWidth
                    variant="contained"
                    disabled={name.trim().length === 0}
                    onClick={handleSave}
                    sx={{ ...blueButtonSx, py: 1.5 }}
                >
                    Save changes
                </Button>

                <Button
                    startIcon={<LogoutRoundedIcon />}
                    onClick={handleLogout}
                    sx={{ minHeight: 44, fontFamily: INTER, fontWeight: 600, fontSize: 14, color: SLATE, alignSelf: 'center' }}
                >
                    Exit preview
                </Button>

            </Stack>
        </PhoneFrame>
    );
}
